import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/session'
import { isGuidanceTemplate, type EmailTemplate } from '../lib/types'
import { formatDateTime } from '../lib/format'
import { insertAtCursor, templateKey } from '../lib/templates'
import {
  isMilestoneKey,
  missingMilestoneTemplates,
  type MilestoneTemplate,
} from '../lib/milestones'
import {
  MERGE_BY_KEY,
  MERGE_FIELDS,
  mergeFieldsUsed,
  unknownMergeFields,
} from '../lib/campaigns'

/**
 * Templates live in the database rather than in code because Renee rewrites
 * them constantly, and anything inside a deployed bundle needs a developer.
 *
 * Three of the rows have keys beginning with an underscore (_guidance_voice,
 * _guidance_belated, _guidance_early). They are notes to whoever writes the
 * emails, not emails. They must stay editable — that is the whole reason they
 * are in this table — but they must never be treated as sendable, so they are
 * separated and carry no send affordance anywhere.
 *
 * **The key is never shown and never typed.** It is the primary key *and* the
 * thing that decides whether a template is guidance, so a stray underscore
 * would produce a template that saves and then never appears in the composer,
 * with nothing on screen explaining why. `templateKey` derives it; the
 * reasoning is there.
 */
export default function Templates() {
  const { appUser } = useAuth()
  const [rows, setRows] = useState<EmailTemplate[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [creatingKey, setCreatingKey] = useState<string | null>(null)
  /**
   * The milestone templates being written, before they exist. **Keyed, and kept
   * when she switches between them** — a single slot was replaced wholesale on
   * the next click, so opening a second entry silently destroyed however much
   * had been typed into the first. This file already says elsewhere that
   * destroying the only copy is worse than the bug it replaced.
   *
   * It is held here rather than inserted empty and edited in place, and that is
   * the whole point: `plan` only asks whether a template KEY exists. A row
   * created with an empty body is immediately visible to the milestone run,
   * which drafts from it and writes the occurrence to `outreach_log` — so a
   * blank card lands in Daily Outreach, the Outbox refuses to send it because
   * there is no message, and that birthday is never detected again. An
   * unwritten template must not exist at all; then `no_template` declines
   * without claiming, and the day the copy is written every event already seen
   * can still fire.
   */
  const [drafts, setDrafts] = useState<Record<string, { subject: string; body: string }>>({})
  const [openKey, setOpenKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('email_templates').select('*').order('key')
    if (error) setError(error.message)
    else setRows((data ?? []) as EmailTemplate[])
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const flash = (m: string) => {
    setToast(m)
    window.setTimeout(() => setToast((t) => (t === m ? null : t)), 1800)
  }

  async function save(t: EmailTemplate) {
    setSavingKey(t.key)
    const { data, error } = await supabase
      .from('email_templates')
      .update({
        subject: t.subject,
        body: t.body,
        updated_by: appUser?.id ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('key', t.key)
      .select('key')
    setSavingKey(null)
    if (error) return setError(error.message)
    /**
     * A zero-row update is a success with no error — the same trap as every
     * other write in this codebase. Saying "Saved" over a template deleted in
     * another window would leave her believing wording exists that does not.
     */
    if (!Array.isArray(data) || data.length === 0) {
      /**
       * Deliberately does NOT reload.
       *
       * The row is gone, so her wording now exists in exactly one place on
       * earth: that textarea. Reloading would replace it with nothing and she
       * could not even copy it out. A "that's gone" message that also destroys
       * the only copy is worse than the silent success it replaced.
       */
      setError(
        `"${t.name}" is no longer there — it may have been deleted in another window. Copy your ` +
          'wording somewhere safe before you leave this page.',
      )
      return
    }
    setError(null)
    flash('Saved')
    /**
     * No reload. `load()` replaces every row from the database, which throws
     * away typing in *other* editors that has not been saved yet — saving
     * Welcome would silently revert half-written changes to Birthday, under a
     * green toast. The screen is already correct for the row that just saved;
     * only the timestamp needs catching up.
     */
    update(t.key, { updated_at: new Date().toISOString() })
  }

  /**
   * The one place a template row is inserted, so the two ways in cannot drift.
   *
   * `create` derives a key from a typed name; `createMilestone` passes the
   * engine's exact key. Everything after that — the duplicate wording, the
   * append rather than reload — has to behave identically, and did not when it
   * was written twice.
   */
  async function insertTemplate(
    key: string,
    name: string,
    subject = '',
    body = '',
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from('email_templates')
      .insert({ key, name, subject, body, updated_by: appUser?.id ?? null })
      .select('*')
    if (error) {
      /**
       * Two presses can still race past the re-read in `create` — Enter
       * auto-repeat used to fire this twenty times — and the raw text of a
       * primary-key violation is not a sentence to put in front of somebody
       * who typed a name.
       */
      setError(
        /duplicate key|unique constraint/i.test(error.message)
          ? 'Something with that name already exists — reload the page to see it.'
          : error.message,
      )
      return false
    }
    // Appended, not reloaded: see the note in `save`.
    const created = ((data ?? []) as EmailTemplate[])[0]
    if (!created) {
      /**
       * The insert did not error and did not hand a row back — a second tab got
       * there first, or the select policy refused to return it. Reporting
       * success left the flash saying "Created", the row absent from the list,
       * and the entry still under "Still to write".
       *
       * **It does NOT call `load()`.** That was the first fix and it was worse
       * than the bug: `load` replaces every row from the database, throwing away
       * unsaved typing in every other editor on the screen — the exact loss
       * `save` documents refusing to cause. Say what happened and let her
       * refresh if she wants to.
       */
      setError(
        'That saved, but the list would not come back — reload the page to see it. Nothing was lost.',
      )
      return false
    }
    setRows((prev) => [...(prev ?? []), created].sort((a, b) => a.key.localeCompare(b.key)))
    return true
  }

  /**
   * Create one of the templates the milestone engine is waiting for, WITH ITS
   * EXACT KEY, and only once it has been written.
   *
   * The key is the whole point. Derived from the name "Birthday belated" it
   * would be `birthday-belated`; the engine looks for `birthday_belated`. The
   * hyphen version saves cleanly, appears on this screen, and is never once
   * read — so a belated birthday goes out worded as though it were the day
   * itself, with nothing on any screen saying why.
   *
   * The subject and body are required for the reason on `drafts` above.
   */
  async function createMilestone(t: MilestoneTemplate) {
    if (creatingKey) return
    const d = drafts[t.key]
    const subject = (d?.subject ?? '').trim()
    const body = (d?.body ?? '').trim()
    if (!subject || !body) {
      return setError(
        'Give it a subject and a message first. A template with nothing in it would be picked up by tomorrow’s milestone emails, drafted blank, and that birthday recorded as handled.',
      )
    }
    setCreatingKey(t.key)
    setError(null)
    const ok = await insertTemplate(t.key, t.name, subject, body)
    setCreatingKey(null)
    if (ok) {
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[t.key]
        return next
      })
      setOpenKey(null)
      flash(`“${t.name}” is written and wired up`)
    }
  }

  async function create() {
    const name = draftName.trim()
    // Re-entry guard. Holding Enter fires key auto-repeat about twenty-five
    // times a second, and every one of them used to start its own insert.
    if (!name || creating) return
    setCreating(true)
    setError(null)
    /**
     * Keys are derived from the names that exist **right now**, re-read rather
     * than taken from whatever this page happens to be holding. Two tabs open
     * on this screen would otherwise pick the same key, and the second insert
     * fails with a primary-key violation in front of somebody who typed a name.
     */
    const fresh = await supabase.from('email_templates').select('key')
    if (fresh.error) {
      setCreating(false)
      return setError(fresh.error.message)
    }
    let key: string
    try {
      key = templateKey(name, ((fresh.data ?? []) as { key: string }[]).map((r) => r.key))
    } catch {
      // Only reachable with ~1000 templates of the same name, but an unhandled
      // throw here leaves the button reading "Creating…" for ever with nothing
      // on screen saying why — a failure with no symptom at all.
      setCreating(false)
      return setError('Too many templates already have that name. Try a different one.')
    }

    /**
     * A MILESTONE KEY IS NEVER CREATED FROM THIS BOX, because this box creates
     * it EMPTY.
     *
     * Three of the fourteen milestone names derive straight onto the engine's
     * live key — "Birthday", "Welcome", "Departed" — and "Welcome" was the
     * worked example printed four lines above the field. Typing one of those
     * here produced a real, engine-visible template with no subject and no
     * body: `plan` drafts from it, `claim` writes the occurrence, the Outbox
     * refuses to send a blank message, and that birthday is never detected
     * again. The other eleven derive to inert hyphenated keys, which is merely
     * useless.
     *
     * So the destructive cases are exactly the ones that *look* like they
     * worked. Refused here and pointed at the panel above, which cannot create
     * one empty.
     */
    if (isMilestoneKey(key)) {
      setCreating(false)
      return setError(
        `“${name}” is one of the milestone emails. Write it under “Still to write” above — created here it would be saved empty, and tomorrow’s milestone emails would use it.`,
      )
    }

    const ok = await insertTemplate(key, name)
    setCreating(false)
    if (!ok) return
    setDraftName('')
    flash('Created — write it below')
  }

  async function remove(t: EmailTemplate) {
    setSavingKey(t.key)
    const { data, error } = await supabase
      .from('email_templates')
      .delete()
      .eq('key', t.key)
      .select('key')
    setSavingKey(null)
    setConfirmDelete(null)
    if (error) return setError(error.message)
    if (!Array.isArray(data) || data.length === 0) setError('That template was already gone.')
    else {
      setError(null)
      flash('Deleted')
    }
    // Removed locally rather than reloaded: see the note in `save`.
    setRows((prev) => (prev ?? []).filter((r) => r.key !== t.key))
  }

  if (error && !rows) return <div className="empty">{error}</div>
  if (!rows) return <div className="empty">Loading…</div>

  const sendable = rows.filter((r) => !isGuidanceTemplate(r.key))
  const guidance = rows.filter((r) => isGuidanceTemplate(r.key))
  /**
   * What the milestone engine is waiting for and cannot find. Nothing on this
   * screen used to say the set was incomplete, so "six templates exist" read as
   * "the templates exist".
   */
  /**
   * Passed the ROWS, not the keys. A milestone template that exists with an
   * empty body is worse than a missing one, and judging on keys alone made this
   * panel go quiet the moment such a row appeared — reproducing the very
   * failure it exists to stop, one level down.
   */
  const missing = missingMilestoneTemplates(rows)
  /**
   * Typing the name of a milestone template into the free-text box produces a
   * HYPHENATED key that the engine will never read. Not blocked — she may
   * genuinely want a second template with a similar name — but said out loud,
   * pointing at the button that does it correctly.
   */
  const tidy = (v: string) => v.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  const looksLikeMilestone = draftName.trim()
    ? missing.find((m) => tidy(m.name) === tidy(draftName))
    : undefined

  const update = (key: string, patch: Partial<EmailTemplate>) =>
    setRows((prev) => (prev ?? []).map((r) => (r.key === key ? { ...r, ...patch } : r)))

  return (
    <div className="board tpl">
      <div className="notice">
        Edited here rather than in code, because these change often. Saving takes effect
        immediately — but nothing in this build sends email.
      </div>

      {error && <p className="error">{error}</p>}

      {missing.length > 0 && (
        <section className="sec" style={{ marginBottom: 18 }}>
          <h3>Still to write</h3>
          <div className="hint" style={{ marginBottom: 8 }}>
            The milestone emails look for these by name. Until one exists the nearest plain
            version is used instead — so a belated birthday goes out worded as though it were the
            day itself. Write it here and it is wired up correctly. It is not created until you
            have put something in it: an empty one would be used by tomorrow's milestone emails
            and drafted blank.
          </div>
          {missing.map((m) => {
            const d = drafts[m.key] ?? { subject: '', body: '' }
            const open = openKey === m.key
            const started = Boolean(d.subject.trim() || d.body.trim())
            const put = (patch: Partial<{ subject: string; body: string }>) =>
              setDrafts((prev) => ({ ...prev, [m.key]: { ...d, ...patch } }))
            return (
              <div key={m.key} style={{ marginBottom: 10 }}>
                <div className="savebar" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <strong>{m.name}</strong>
                    <div className="hint">{m.when}</div>
                  </div>
                  {/*
                    Collapsing only hides it. What was typed stays in `drafts`,
                    keyed, so opening another entry — or this one again — does
                    not destroy it. A single shared slot was replaced wholesale
                    on the next click and took the typing with it.
                  */}
                  <button
                    className="btn"
                    disabled={creatingKey !== null}
                    onClick={() => setOpenKey(open ? null : m.key)}
                  >
                    {open ? 'Hide' : started ? 'Keep writing' : 'Write it'}
                  </button>
                </div>
                {!open && started && (
                  <p className="hint" style={{ marginTop: 4 }}>
                    Started, not saved yet.
                  </p>
                )}
                {open && (
                  <div style={{ marginTop: 6 }}>
                    <label>Subject</label>
                    <input
                      value={d.subject}
                      onChange={(e) => put({ subject: e.target.value })}
                    />
                    <label>Message</label>
                    <textarea
                      rows={6}
                      value={d.body}
                      onChange={(e) => put({ body: e.target.value })}
                    />
                    {/*
                      It does not exist until this is pressed. An empty template
                      would be picked up by the milestone run, drafted blank, and
                      the occurrence written off as handled — see `drafts` above.
                    */}
                    <div className="savebar" style={{ marginTop: 6 }}>
                      <button
                        className="btn"
                        disabled={creatingKey !== null || !d.subject.trim() || !d.body.trim()}
                        onClick={() => void createMilestone(m)}
                      >
                        {creatingKey === m.key ? 'Saving…' : 'Save it'}
                      </button>
                      <span className="hint">
                        Saved as <strong>{m.name}</strong>. You can keep editing it below
                        afterwards.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </section>
      )}

      <section className="sec" style={{ marginBottom: 18 }}>
        <h3>New template</h3>
        <div className="hint" style={{ marginBottom: 6 }}>
          Give it a name you will recognise in the list — "Licence renewal", "Open house
          follow-up". Write the subject and the message once it exists. For the milestone
          emails, use <strong>Still to write</strong> above instead.
        </div>
        <div className="savebar">
          <input
            style={{ flex: 1 }}
            value={draftName}
            placeholder="e.g. Licence renewal"
            onChange={(e) => setDraftName(e.target.value)}
            disabled={creating}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.repeat && !creating) void create()
            }}
          />
          <button
            className="btn"
            disabled={creating || !draftName.trim()}
            onClick={() => void create()}
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
        {/*
          The name is the only thing shown for a template — here and in the
          composer's picker. Two called "Welcome" are indistinguishable on both
          screens, and picking the wrong one sends last month's wording to 386
          people. Not blocked, because a duplicate is occasionally what she
          means; said out loud, because by default it is not.
        */}
        {looksLikeMilestone && (
          <p className="hint" style={{ marginTop: 6 }}>
            “{looksLikeMilestone.name}” is one of the milestone emails. Write it under{' '}
            <strong>Still to write</strong> above — this box would save it with nothing in it,
            and an empty milestone template is used by tomorrow’s emails and drafted blank.
          </p>
        )}
        {draftName.trim() &&
          rows.some((r) => r.name.trim().toLowerCase() === draftName.trim().toLowerCase()) && (
            <p className="hint" style={{ marginTop: 6 }}>
              There is already a template called “{draftName.trim()}”. Two with the same name look
              identical everywhere they are offered — worth adding a word to tell them apart.
            </p>
          )}
      </section>

      {sendable.map((t) => (
        <Editor
          key={t.key}
          t={t}
          saving={savingKey === t.key}
          confirming={confirmDelete === t.key}
          onChange={update}
          onSave={save}
          onAskDelete={() => setConfirmDelete(t.key)}
          onCancelDelete={() => setConfirmDelete(null)}
          onDelete={remove}
        />
      ))}

      <div className="notice guidance" style={{ marginTop: 24 }}>
        <strong>Guidance, not emails.</strong> These are notes about how to write. They are
        editable because they change often, nothing will ever send them, and they cannot be
        deleted — there would be no way to get one back.
      </div>
      {guidance.map((t) => (
        <Editor
          key={t.key}
          t={t}
          saving={savingKey === t.key}
          onChange={update}
          onSave={save}
          guidance
        />
      ))}

      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}

function Editor({
  t,
  saving,
  guidance,
  confirming,
  onChange,
  onSave,
  onAskDelete,
  onCancelDelete,
  onDelete,
}: {
  t: EmailTemplate
  saving: boolean
  guidance?: boolean
  confirming?: boolean
  onChange: (key: string, patch: Partial<EmailTemplate>) => void
  onSave: (t: EmailTemplate) => void
  onAskDelete?: () => void
  onCancelDelete?: () => void
  onDelete?: (t: EmailTemplate) => void
}) {
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)
  const subjectRef = useRef<HTMLInputElement | null>(null)
  /** Which field the merge buttons act on — whichever she touched last. */
  const [target, setTarget] = useState<'subject' | 'body'>('body')

  const used = guidance ? [] : mergeFieldsUsed(`${t.subject}\n${t.body}`)
  /**
   * Split, because they mean opposite things.
   *
   * `mergeFieldsUsed` matches any `{{word}}`, known or not. Telling her a
   * template with `{{First_Name}}` in it is "a read-and-send one" is the
   * reverse of true — the composer raises a **stop** for an unknown field in
   * *both* lanes and refuses to queue it. This editor is the only screen that
   * can catch a typo at the moment it is made; reassuring her instead is worse
   * than saying nothing.
   */
  const known = used.filter((u) => MERGE_BY_KEY.has(u))
  /** `campaigns.ts` decides this, here and at Send and on the way to the clipboard. */
  const unknown = unknownMergeFields(`${t.subject}\n${t.body}`)

  const drop = (fieldKey: string) => {
    const el = target === 'subject' ? subjectRef.current : bodyRef.current
    const source = target === 'subject' ? t.subject : t.body
    /**
     * Only a *focused* field has a cursor.
     *
     * An untouched textarea reports `selectionStart` of 0, which is
     * indistinguishable from a caret at the start unless the caller checks.
     * Reading it blindly put every merge field at character zero on the first
     * press. `null` says "no cursor" and the helper appends.
     */
    const live = !!el && document.activeElement === el
    const { text, caret } = insertAtCursor(
      source,
      live ? el.selectionStart : null,
      live ? el.selectionEnd : null,
      `{{${fieldKey}}}`,
    )
    onChange(t.key, target === 'subject' ? { subject: text } : { body: text })
    /*
      Put the cursor after what was just inserted, once React has painted the
      new value. Without this the caret stays where it was and the next button
      press inserts *before* the first one — "{{last_name}}{{first_name}}".
    */
    window.requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(caret, caret)
    })
  }

  /**
   * "Add a hyperlink" — but not the Word kind, and deliberately not: this
   * inserts a plain URL, never custom link text hiding a different address.
   *
   * The reason is which pipe a template's email actually travels down.
   * Reviewed-lane sends (birthdays, anniversaries, drips — nearly everything
   * edited here) go out through a Gmail compose link with this text as a URL
   * parameter, plain and unparsed; Gmail autolinks a bare URL the moment that
   * draft opens, but there is no way to hand it "call this text a link to
   * that address" through that channel — Gmail would show the raw markup
   * instead of a link. A blast-lane send, the other path a template can take,
   * DOES get assembled into real HTML (`textToHtmlParagraphs` in
   * `emailrules.ts`), but a template is not committed to one lane or the
   * other until somebody sends it, so the one thing this button can insert
   * that is guaranteed to render as a working link EITHER way it ends up
   * being sent is the URL itself. Decided with Chris 2026-09-07 after the
   * Word-style version turned out to only work for one of the two lanes.
   *
   * Highlighted text is kept rather than replaced — `"<selected> (<url>)"` —
   * so the label she wrote stays readable and the address sits right next to
   * it, instead of the selection just vanishing into a URL with no context.
   */
  const insertLink = () => {
    const el = target === 'subject' ? subjectRef.current : bodyRef.current
    const source = target === 'subject' ? t.subject : t.body
    const live = !!el && document.activeElement === el
    const hasSelection = live && el!.selectionStart !== el!.selectionEnd
    const selectedText = hasSelection ? source.slice(el!.selectionStart ?? 0, el!.selectionEnd ?? 0) : ''

    const raw = window.prompt(hasSelection ? `Link "${selectedText}" to:` : 'Paste the URL to insert:')
    if (raw === null) return
    const url = raw.trim()
    if (!url) return

    // Typed without a scheme — "reneeroark.com" rather than
    // "https://reneeroark.com" — Gmail and a blast HTML client both leave it
    // as inert plain text instead of a link. Assumed rather than left broken.
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`
    const insertText = hasSelection ? `${selectedText} (${withScheme})` : withScheme

    const { text, caret } = insertAtCursor(
      source,
      live ? el!.selectionStart : null,
      live ? el!.selectionEnd : null,
      insertText,
    )
    onChange(t.key, target === 'subject' ? { subject: text } : { body: text })
    window.requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(caret, caret)
    })
  }

  return (
    <section className="sec" style={{ marginBottom: 14 }}>
      <h3>
        {t.name}
        {guidance && <span className="badge dep">GUIDANCE</span>}
      </h3>

      {!guidance && (
        <>
          <div className="hint" style={{ marginBottom: 4 }}>Subject</div>
          <input
            ref={subjectRef}
            value={t.subject}
            onFocus={() => setTarget('subject')}
            onChange={(e) => onChange(t.key, { subject: e.target.value })}
          />
        </>
      )}

      <div className="hint" style={{ marginBottom: 4, marginTop: 10 }}>
        {guidance ? 'Notes' : 'Body'}
      </div>
      <textarea
        ref={bodyRef}
        rows={guidance ? 8 : 12}
        value={t.body}
        onFocus={() => setTarget('body')}
        onChange={(e) => onChange(t.key, { body: e.target.value })}
      />

      {!guidance && (
        <>
          <p className="hint">
            Drop into the {target === 'subject' ? 'subject' : 'message'}, where the cursor is:{' '}
            {MERGE_FIELDS.map((f) => (
              <button
                key={f.key}
                className="btn ghost sm"
                style={{ marginRight: 4 }}
                /*
                  Without this, the click itself is what breaks the cursor.
                  Clicking a <button> moves focus to it BEFORE the onClick
                  handler runs — so by the time `drop` reads
                  `document.activeElement`, the textarea is no longer it, `live`
                  comes back false, and the field falls back to appending at
                  the end. Every "insert at cursor" button next to a text field
                  needs this same guard; `preventDefault` on mousedown is what
                  stops the browser from shifting focus off the field in the
                  first place, so the selection `drop` reads a moment later is
                  still the real one.
                */
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => drop(f.key)}
              >
                {f.label}
              </button>
            ))}
            <button
              className="btn ghost sm"
              style={{ marginRight: 4 }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={insertLink}
            >
              Insert link
            </button>
          </p>
          <p className="hint">
            Highlight the words you want linked first if you want them kept — the link itself is
            always a plain web address (Gmail and the outreach mailer both turn a plain address
            into a live link on their own; custom link text like Word's isn't possible here).
          </p>

          {/*
            Said here, while she is writing, rather than discovered later.

            The composer hides any template containing a merge field when the
            lane is "same message to everybody" — deliberately, because nobody
            reads a blast before it leaves and a raw fill-in going out to 386
            people is unacceptable. Without this line she writes "Happy birthday
            {{first_name}}", picks that lane, finds her template missing from
            the list, and concludes it did not save.
          */}
          {unknown.length > 0 && (
            <p className="error">
              <strong>{unknown.map((u) => `{{${u}}}`).join(', ')}</strong>{' '}
              {unknown.length === 1 ? 'is not something' : 'are not things'} we know about, so
              nothing can fill {unknown.length === 1 ? 'it' : 'them'} in. Use the buttons above
              rather than typing them — spelling and capitals both matter.{' '}
              <strong>Until this is fixed the template cannot be sent at all.</strong>
            </p>
          )}
          {known.length > 0 && (
            <p className="hint">
              <strong>Uses {known.map((u) => `{{${u}}}`).join(', ')}.</strong> That makes this one
              a <em>read and send each one</em> template. It will not be offered when you choose
              to send everybody the same message, because nobody reads those before they go.
            </p>
          )}
        </>
      )}

      <div className="savebar">
        <button className="btn" onClick={() => onSave(t)} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>

        {/*
          Delete is offered on sendable templates only. The three guidance notes
          stay — there is no way back from deleting one except retyping it, and
          they are the least likely thing here to be finished with.

          Deleting a template destroys nothing else: picking one *copies* its
          subject and body into the draft, so every email ever sent from it
          keeps its own wording forever.
        */}
        {!guidance && confirming && (
          <>
            <span className="cnt" style={{ margin: 0 }}>Delete “{t.name}”?</span>
            <button className="btn" disabled={saving} onClick={() => onDelete?.(t)}>
              Yes, delete it
            </button>
            <button className="btn ghost" disabled={saving} onClick={onCancelDelete}>
              Keep it
            </button>
          </>
        )}
        {!guidance && !confirming && (
          <button className="btn ghost" disabled={saving} onClick={onAskDelete}>
            Delete
          </button>
        )}

        <span className="status">Updated {formatDateTime(t.updated_at)}</span>
      </div>
    </section>
  )
}
